import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { I18NEXT_NAMESPACE, I18NextModule } from 'angular-i18next';
import { AdminLookupService } from 'app/admin/lookups/admin-lookup-service';
import { ObjectDrawerFilterDialogComponent } from 'app/component-library/object-drawer/object-drawer-filter-dialog/object-drawer-filter-dialog.component';
import { ContentHeightDirective } from 'app/directives/content-height/content-height.directive';
import { MaintenanceStrategyAvailablePlannedMaintenanceTableComponent } from 'app/maintenance-strategies-library/details/planned-maintenance/table/available/available.component';
import { Ng2GoogleChartsModule } from 'ng2-google-charts';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { NgxFileDropModule } from 'ngx-file-drop';
import {
  MessageService,
  SharedModule,
} from 'primeng/api';

import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AccordionModule } from 'primeng/accordion';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ListboxModule } from 'primeng/listbox';
import { MessagesModule } from 'primeng/messages';
import { MultiSelectModule } from 'primeng/multiselect';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SpinnerModule } from 'primeng/spinner';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';
import { HierarchyTemplateAttributesTableConfigService } from '../admin/hierarchy-templates/hierarchy-template-attributes-table-config.service';
import { HierarchyTemplateAttributesTableService } from '../admin/hierarchy-templates/hierarchy-template-attributes-table.service';
import { HierarchyTemplateDetailsService } from '../admin/hierarchy-templates/hierarchy-template-details.service';
import { HierarchyTemplatesNotificationService } from '../admin/hierarchy-templates/hierarchy-templates-notification.service';
import { HierarchyTemplatesService } from '../admin/hierarchy-templates/hierarchy-templates.service';
import { AdminLookupTableService } from '../admin/lookups/admin-lookup-table-service';
import { AdminModelConfigService } from '../admin/models/model-details/admin-model-config.service';
import { AdminModelDetailsActionsComponent } from '../admin/models/model-details/admin-model-details-actions.component';
import { TableAttributeTemplateTableConfigService } from '../admin/table-attribute-templates/table-attribute-template-table-config.service';
import { TableAttributeTemplateTableService } from '../admin/table-attribute-templates/table-attribute-template-table.service';
import { TableAttributeTemplateService } from '../admin/table-attribute-templates/table-attribute-template.service';
import { TableAttributeTemplatesProviderService } from '../admin/table-attribute-templates/table-attribute-templates-provider.service';
import { HbComponentLibraryModule } from '../component-library/component-library.module';
import { BootstrapImportsModule } from '../core/bootstrap-imports.module';
import { TabOrderDirective } from '../directives';
import { FeaturesModule } from '../features/features.module';
import { MaintenanceStrategyPlannedMaintenanceDialogComponent } from '../maintenance-strategies-library/details/planned-maintenance/planned-maintenance-dialog/maintenance-strategy-planned-maintenance-dialog.component';
import { MaintenanceStrategyPlannedMaintenanceTableComponent } from '../maintenance-strategies-library/details/planned-maintenance/table/maintenance-strategy-planned-maintenance-table.component';
import { PlannedMaintenanceLibraryService } from '../planned-maintenance-library/planned-maintenance-library.service';
import { ConditionForWorkPipe } from '../viewmodels/planned-maintenance/condition-for-work-pipe';
import { MaintenanceTypePipe } from '../viewmodels/planned-maintenance/maintenance-type-pipe';
import { PlannedMaintenanceFrequencyPipe } from '../viewmodels/planned-maintenance/planned-maintenance-frequency.pipe';
import { AdminLookupTableConfigService } from './../admin/lookups/admin-lookup-table-config-service';
import { AuditReportTableComponent } from './audit-report-table/audit-report-table.component';
import { CatalogSelectorComponent } from './catalog-selector/catalog-selector.component';
import { ChartViewerComponent } from './chart-viewer/chart-viewer.component';
import { ChildDataTableMultiSelectionDialogComponent } from './child-data-table/child-data-table-multi-selection-dialog/child-data-table-multi-selection-dialog.component';
import { ChildDataTableComponent } from './child-data-table/child-data-table.component';
import { CommercialImpactInputsComponent } from './commercial-impacts/commercial-impact-inputs/commercial-impact-inputs.component';
import { CommercialImpactItemTypePipe } from './commercial-impacts/commercial-impact-item/commercial-impact-item-type.pipe';
import { CommercialImpactItemComponent } from './commercial-impacts/commercial-impact-item/commercial-impact-item.component';
import { CommercialImpactsComponent } from './commercial-impacts/commercial-impacts/commercial-impacts.component';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogService } from './confirmation-dialog/confirmation-dialog.service';
import { IconWithBadgeComponent } from './custom-icons/icon-with-badge/icon-with-badge.component';
import { InspectionHistoryIconComponent } from './custom-icons/inspection-history-icon/inspection-history-icon.component';
import { HierarchyDashboardDrilldownService } from './dashboard-drilldown/dashboard-drilldown.service';
import { DataUploadMessageSeveritySymbolComponent } from './data-uploads/results/data-upload-message-severity-symbol/data-upload-message-severity-symbol.component';
import { DataUploadMessageTableComponent } from './data-uploads/results/data-upload-message-table/data-upload-message-table.component';
import { DataUploadResultComponent } from './data-uploads/results/data-upload-result.component';
import { DataUploadRowMessageTableComponent } from './data-uploads/results/data-upload-row-message-table/data-upload-row-message-table.component';
import { DataUploadWorksheetResultComponent } from './data-uploads/results/data-upload-worksheet-result/data-upload-worksheet-result.component';
import { DisplayAppliedFilterComponent } from './display-applied-filter/display-applied-filter.component';
import { DisplayModelDetailsComponent } from './display-model-details/display-model-details.component';
import { EquipmentClassComponent } from './display-model-details/equipment-class/equipment-class.component';
import { IndustryHierarchyComponent } from './display-model-details/industry-hierarchy/industry-hierarchy.component';
import { DisplayModelsListComponent } from './display-models-list/display-models-list.component';
import { DynamicDetailsListFilterService } from './dynamic-details-list/dynamic-details-list-filter.service';
import { DynamicDetailsListItemComponent } from './dynamic-details-list/dynamic-details-list-item.component';
import { DynamicDetailsListComponent } from './dynamic-details-list/dynamic-details-list.component';
import { DynamicDetailsListService } from './dynamic-details-list/dynamic-details-list.service';
import { FilterAttributeFactoryService } from './dynamic-details-list/filter-attribute-factory.service';
import { FilterTagComponent } from './dynamic-details-list/filter-tag.component';
import { DynamicDetailsTreeComponent } from './dynamic-details-tree/dynamic-details-tree.component';
import { DynamicDetailsDialogComponent } from './dynamic-details/dynamic-details-dialog.component';
import { DynamicDetailsSaveComponent } from './dynamic-details/dynamic-details-save.component';
import { DynamicDetailsComponent } from './dynamic-details/dynamic-details.component';
import { DynamicFormControlService } from './dynamic-forms/dynamic-form-control.service';
import { DynamicFormControlLabelComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-label/dynamic-form-control-label.component';
import { DropdownDoubleComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dropdown-double.component';
import { DropdownComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dropdown.component';
import { DynamicAutoCompleteDoubleComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-auto-complete-double.component';
import { DynamicAutoCompleteTextboxComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-auto-complete-textbox.component';
import { DynamicAutoCompleteComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-auto-complete.component';
import { DynamicCheckBoxComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-check-box.component';
import { DynamicDateTimeComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-date-time.component';
import { DynamicFormControlViewsService } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-form-control-views.service';
import { DynamicLabelComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-label.component';
import { DynamicMinMaxComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-min-max.component';
import { DynamicNumberComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-number.component';
import { DynamicPasswordComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-password.component';
import { DynamicPrimeListBoxComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-prime-list-box.component';
import { DynamicRadioButtonComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-radio-button.component';
import { DynamicRichTextComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-rich-text.component';
import { DynamicSeparatorComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/dynamic-separator.component';
import { FilterDropdownComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/filter-dropdown.component';
import { LoadingDropdownComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/loading-dropdown.component';
import { MultiSelectComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/multi-select.component';
import { PrimeDropdownComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/prime-dropdown.component';
import { TextareaComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/textarea.component';
import { TextboxComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control-views/textbox.component';
import { DynamicFormControlComponent } from './dynamic-forms/form-components/dynamic-form-control/dynamic-form-control.component';
import { DynamicFormComponent } from './dynamic-forms/form-components/dynamic-form/dynamic-form.component';
import { EquipmentFunctionModelSelectionCriteriaDialogComponent } from './equipment-functions/equipment-function-model-selection-criteria-dialog.component';
import { IsoHierarchyQueryComponent } from './equipment-functions/iso-hierarchy-query.component';
import { EventsHistoryDialogComponent } from './events-history-dialog/events-history-dialog.component';
import { ExpressionValidatorService } from './expression-validation/expression-validator.service';
import { FileSelectionComponent } from './file-upload/file-selection.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { FilterSearchBoxComponent } from './filter-search-box/filter-search-box.component';
import { HbCheckboxComponent } from './forms/hb-checkbox/hb-checkbox.component';
import { HbNumberInputComponent } from './forms/hb-number-input/hb-number-input.component';
import { HbRadioButtonComponent } from './forms/hb-radio-button/hb-radio-button.component';
import { HbSingleSelectDropdownComponent } from './forms/hb-single-select-dropdown/hb-single-select-dropdown.component';
import { HbTextareaComponent } from './forms/hb-textarea/hb-textarea.component';
import { HbTextboxInputComponent } from './forms/hb-textbox-input/hb-textbox-input.component';
import { FunctionEditorComponent } from './function-editor/function-editor.component';
import { FunctionEditorService } from './function-editor/function-editor.service';
import { HbMathJaxDirective } from './hbmathjax/hbmathjax.directive';
import { HbmathjaxService } from './hbmathjax/hbmathjax.service';
import { FlattenedOrgTreeNodeListComponent } from './hierarchy-list/flattened-org-tree-node-list.component';
import { HierarchyTableComponent } from './hierarchy-list/hierarchy-table.component';
import { IdmsBaseItemDetailsComponent } from './idms/idms-base-item-details/idms-base-item-details.component';
import { InspectionHistoryButtonComponent } from './inspection-history/inspection-history-button/inspection-history-button.component';
import { InspectionHistoryDialogComponent } from './inspection-history/inspection-history.component';
import { ReportDrawingsComponent } from './inspection-reports/drawings/report-drawings.component';
import { SiteHierarchyItemSelectionComponent } from './inspection-reports/report-details/site-hierarchy-item-selection.component';
import { InspectionReportListService } from './inspection-reports/report-list/inspection-report-list.service';
import { ReportTasksComponent } from './inspection-reports/tasks/report-tasks.component';
import { ReportWorkOrdersComponent } from './inspection-reports/work-orders/report-work-orders.component';
import { LanguageSelectorComponent } from './language-selector/language-selector.component';
import { LookupCopyConfirmationDialogComponent } from './lookup-copy-confirmation-dialog/lookup-copy-confirmation-dialog.component';
import { MediaActionsComponent } from './media/media-actions.component';
import { MediaButtonComponent } from './media/media-button.component';
import { MediaDetailsComponent } from './media/media-details.component';
import { MediaItemService } from './media/media-item.service';
import { MediaComponent } from './media/media.component';
import { DataStorageMediaPreviewComponent } from './media/preview/data-storage-media-preview.component';
import { DrawingPreviewInspectionPointService } from './media/preview/drawings/drawing-preview-inspection-point.service';
import { DrawingPreviewLegendComponent } from './media/preview/drawings/drawing-preview-legend.component';
import { DrawingPreviewNavigationComponent } from './media/preview/drawings/drawing-preview-navigation.component';
import { DrawingPreviewNavigationService } from './media/preview/drawings/drawing-preview-navigation.service';
import { DrawingPreviewComponent } from './media/preview/drawings/drawing-preview.component';
import { DrawingPreviewService } from './media/preview/drawings/drawing-preview.service';
import { DrawingPreviewGalleryComponent } from './media/preview/drawings/gallery/drawing-preview-gallery.component';
import { DrawingPreviewGalleryService } from './media/preview/drawings/gallery/drawing-preview-gallery.service';
import { InspectionPointOverlayComponent } from './media/preview/drawings/inspection-point-overlay.component';
import { FileLinkMediaPreviewComponent } from './media/preview/file-link-media-preview.component';
import { MitigationPlanComponent } from './mitigation-plan/mitigation-plan.component';
import { MitigationPlanService } from './mitigation-plan/mitigation-plan.service';
import { MitigationsTableComponent } from './mitigations/mitigations-table.component';
import { ModalDialogComponent } from './modal-dialog/modal-dialog.component';
import { ModelFailureModeConsequenceClassificationComponent } from './model-functions/classify-consequences/model-failure-mode-consequence-classification.component';
import { ModelFailureMechanismReasonFailureCauseDetailsComponent } from './model-functions/failure-causes/model-failure-mechanism-reason-failure-cause-details.component';
import { ConsequenceRiskMatrixSelectionDialogComponent } from './model-functions/failure-effect/consequences/consequence-risk-matrix-selection-dialog.component';
import { FailureEffectCommercialImpactDialogComponent } from './model-functions/failure-effect/failure-effect-commercial-impact-dialog.component';
import { ModelFailureEffectDetailsComponent } from './model-functions/failure-effect/model-failure-effect-details.component';
import { ModelFailureMechanismReasonDetailsComponent } from './model-functions/failure-mechanism-reasons/model-failure-mechanism-reason-details.component';
import { ModelFailureModeFailureMechanismDetailsComponent } from './model-functions/failure-mechanisms/model-failure-mode-failure-mechanism-details.component';
import { FailureModeParametersDialogComponent } from './model-functions/failure-modes/failure-mode-parameters-dialog.component';
import { FailureRateInputComponent } from './model-functions/failure-modes/inputs/failure-rate-input/failure-rate-input.component';
import { ModelFailureModeDetailsComponent } from './model-functions/failure-modes/model-failure-mode-details.component';
import { ModelFunctionalFailureDetailsComponent } from './model-functions/functional-failures/model-functional-failure-details.component';
import { ModelFunctionDetailsContainerComponent } from './model-functions/functions/model-function-details-container.component';
import { ModelFunctionDetailsComponent } from './model-functions/functions/model-function-details.component';
import { ModelFunctionDeletionService } from './model-functions/model-function-deletion.service';
import { PlannedMaintenanceFailureModeSelectionDialogComponent } from './model-functions/planned-maintenance/failure-mode-selection/planned-maintenance-failure-mode-selection-dialog.component';
import { ModelPlannedMaintenanceDetailsComponent } from './model-functions/planned-maintenance/model-planned-maintenance-details.component';
import { ModelLookupsPackageUploadComponent } from './model-packages/model-lookups-package-upload.component';
import { ModelPackageUploadComponent } from './model-packages/model-package-upload.component';
import { ModelPackageService } from './model-packages/model-package.service';
import { PackageUploadComponent } from './model-packages/package-upload.component';
import { RcmPackageUploadComponent } from './model-packages/rcm-package-upload.component';
import { PdfViewerComponent } from './pdf-viewer/pdf-viewer.component';
import { PerformanceStandardsDialogComponent } from './performance-standards/performance-standards-dialog.component';
import { AuditEventApplicationDisplayTextPipe } from './pipes/audit-event-application-display-text.pipe';
import { BatchJobStatePipe } from './pipes/batch-job-state.pipe';
import { BatchJobTypePipe } from './pipes/batch-job-type.pipe';
import { ConcatenateParentChildPipe } from './pipes/concatenate-parent-child.pipe';
import { CreateNumericLabelPipe } from './pipes/create-numeric-label.pipe';
import { DateTimeFormatterPipe } from './pipes/date-time-formatter.pipe';
import { ErpReferencePipe } from './pipes/erp-reference.pipe';
import { ErpSyncStateIconPipe } from './pipes/erp-sync-state-icon.pipe';
import { ErpSyncStateMessagePipe } from './pipes/erp-sync-state-message.pipe';
import { ErpSyncStatePipe } from './pipes/erp-sync-state.pipe';
import { IdentifierColumnDisabledPipe } from './pipes/events/identifier-column-disabled.pipe';
import { IdentifierColumnLabelPipe } from './pipes/events/identifier-column-label.pipe';
import { FailureRateUomPipe } from './pipes/failure-rate-uom.pipe';
import { GenericDictionaryPipe } from './pipes/generic-dictionary.pipe';
import { GetInspectionPlanTaskCellTextPipe } from './pipes/get-inspection-plan-task-cell-text.pipe';
import { InvalidNumberCellPipe } from './pipes/invalid-number-cell.pipe';
import { IsRestrictedRolePipe } from './pipes/is-restricted-role.pipe';
import { LicenceStatusPipe } from './pipes/licence-status.pipe';
import { LinkTemplatePipe } from './pipes/link-template.pipe';
import { MaintenanceAnalysisStatusPipe } from './pipes/maintenance-analysis-status.pipe';
import { ValidateOptimizationTypePipe } from './pipes/maintenance-optimization/validate-optimization-type.pipe';
import { MapPipe } from './pipes/map.pipe';
import { MitigationPlanTaskFrequencyPipe } from './pipes/mitigation-plan-task-frequency.pipe';
import { ModelStatusPipe } from './pipes/model-status.pipe';
import { NameVersionPipe } from './pipes/name-version.pipe';
import { NodeInputDataTypePipe } from './pipes/node-input-data-type.pipe';
import { NodeOutputDataTypePipe } from './pipes/node-output-data-type.pipe';
import { NullUndefinedToStringPipe } from './pipes/null-undefined-to-string.pipe';
import { PlannedMaintenanceActivityPipe } from './pipes/planned-maintenance-activity.pipe';
import { PlannedMaintenanceIntervalPipe } from './pipes/planned-maintenance-interval.pipe';
import { RecordCountPipe } from './pipes/record-count.pipe';
import { RequirementTypePipe } from './pipes/requirement-type-pipe';
import { ScenarioTypePipe } from './pipes/scenario-type.pipe';
import { SplitPipe } from './pipes/split.pipe';
import { CreateThicknessLabelPipe } from './pipes/thickness-management/create-thickness-label.pipe';
import { TMinLabelPipe } from './pipes/thickness-management/tmin-label.pipe';
import { ThicknessReportTypePipe } from './pipes/thickness-report-type.pipe';
import { TimeConverterPipe } from './pipes/time-converter.pipe';
import { TranslateTerminologyPipe } from './pipes/translate-terminology.pipe';
import { TrueFalsePipe } from './pipes/true-false.pipe';
import { WidgetDateRangeValidatorPipe } from './pipes/widget-date-range-validator.pipe';
import { WidgetSettingsPipe } from './pipes/widget/widget-settings.pipe';
import { YesNoPipe } from './pipes/yes-no.pipe';
import { ReportHierarchyTableComponent } from './report-hierarchy-table/report-hierarchy-table.component';
import { ConsequenceAxisLabelService } from './risk-matrices/consequence-axis-label.service';
import { RiskMatrixGridComponent } from './risk-matrices/risk-matrix-grid.component';
import { XAxisLabelPipe } from './risk-matrices/xaxis-label.pipe';
import { DynamicDetailsSectionContainerComponent } from './section-container/dynamic-details-section-container.component';
import { SectionHeaderAddItemComponent } from './section-header/section-header-add-item.component';
import { SectionHeaderComponent } from './section-header/section-header.component';
import { SectionHeaderService } from './section-header/section-header.service';
import { SiteDropdownListComponent } from './sites/site-dropdown-list.component';
import { StatusLoaderComponent } from './status-loader/status-loader.component';
import { SwitcherComponent } from './switcher/switcher.component';
import { ThicknessDisplayService } from './thickness-management/thickness-display.service';
import { HbSharedUiModule } from './ui/hb-shared-ui.module';
import { UomService } from './uom/uom.service';
import { ConfirmPasswordValidator } from './validators/confirm-password.validator';
import { FailureDefectsValidatorDirective } from './validators/failure-defects.validator';
import { FailureEffectCommentValidator } from './validators/failure-effect-comment.validator';
import { FailureEffectCommercialImpactValidator } from './validators/failure-effect-commercial-impacts.validator';
import { FailureEffectConsequenceValidator } from './validators/failure-effect-consequence.validator';
import { FailurePatternOtherParamsValidator } from './validators/failure-pattern-other.validator';
import { FailurePatternWeibullValidator } from './validators/failure-pattern-weibull.validator';
import { FailureRateUomValidatorDirective } from './validators/failure-rate-uom.validator';
import { IsIntValidator } from './validators/is-int-validator';
import { IsValidPasswordValidator } from './validators/is-valid-password.validator';
import { PlannedMaintenanceFrequencyCalendarValidator } from './validators/planned-maintenance/planned-maintenance-frequency-calendar.validator';
import { PlannedMaintenanceFrequencyHoursRunValidator } from './validators/planned-maintenance/planned-maintenance-frequency-hours-run.validator';
import { PlannedMaintenanceFrequencyUsageFactorValidator } from './validators/planned-maintenance/planned-maintenance-frequency-usage-factor.validator';
import { PrecisionValidator } from './validators/precision.validator';
import { RangeValidator } from './validators/range.validator';
import { UsernameIsEmailValidator } from './validators/username-is-email.validator';


const MaterialModules = [
  MatNativeDateModule,
  MatFormFieldModule,
  MatInputModule,
  MatDatepickerModule,
  MatButtonModule,
];

@NgModule({
  declarations: [
    AdminModelDetailsActionsComponent,
    ConcatenateParentChildPipe,
    ConditionForWorkPipe,
    ConfirmationDialogComponent,
    DataStorageMediaPreviewComponent,
    CommercialImpactsComponent,
    CommercialImpactInputsComponent,
    DateTimeFormatterPipe,
    DisplayModelDetailsComponent,
    DisplayModelsListComponent,
    DrawingPreviewComponent,
    DrawingPreviewGalleryComponent,
    DrawingPreviewLegendComponent,
    DrawingPreviewNavigationComponent,
    DynamicAutoCompleteComponent,
    TabOrderDirective,
    DynamicAutoCompleteDoubleComponent,
    DynamicAutoCompleteTextboxComponent,
    DynamicCheckBoxComponent,
    DynamicDateTimeComponent,
    DynamicDetailsComponent,
    DynamicDetailsDialogComponent,
    DynamicDetailsListComponent,
    DynamicDetailsListItemComponent,
    DynamicDetailsSaveComponent,
    DynamicDetailsSectionContainerComponent,
    DropdownComponent,
    DropdownDoubleComponent,
    FilterDropdownComponent,
    DynamicFormComponent,
    DynamicFormControlComponent,
    DynamicLabelComponent,
    DynamicMinMaxComponent,
    DynamicNumberComponent,
    DynamicPasswordComponent,
    PrimeDropdownComponent,
    DynamicPrimeListBoxComponent,
    DynamicRadioButtonComponent,
    DynamicRichTextComponent,
    DynamicSeparatorComponent,
    TextareaComponent,
    TextboxComponent,
    DynamicFormControlLabelComponent,
    ErpReferencePipe,
    EquipmentClassComponent,
    EquipmentFunctionModelSelectionCriteriaDialogComponent,
    FailureDefectsValidatorDirective,
    FailureModeParametersDialogComponent,
    FailurePatternOtherParamsValidator,
    FailurePatternWeibullValidator,
    FailureEffectConsequenceValidator,
    FailureEffectCommentValidator,
    FailureEffectCommercialImpactValidator,
    FailureRateUomValidatorDirective,
    FileLinkMediaPreviewComponent,
    FileSelectionComponent,
    FileUploadComponent,
    FilterTagComponent,
    FlattenedOrgTreeNodeListComponent,
    FunctionEditorComponent,
    HbMathJaxDirective,
    HierarchyTableComponent,
    IndustryHierarchyComponent,
    InspectionPointOverlayComponent,
    LicenceStatusPipe,
    LinkTemplatePipe,
    LookupCopyConfirmationDialogComponent,
    MaintenanceTypePipe,
    MediaActionsComponent,
    MediaButtonComponent,
    MediaComponent,
    MediaDetailsComponent,
    MitigationsTableComponent,
    ModelFailureModeDetailsComponent,
    ModelFunctionDetailsComponent,
    ModelFunctionalFailureDetailsComponent,
    ModelFailureModeFailureMechanismDetailsComponent,
    ModelFailureMechanismReasonDetailsComponent,
    ModelFailureMechanismReasonFailureCauseDetailsComponent,
    ModelFailureEffectDetailsComponent,
    ModelFailureModeConsequenceClassificationComponent,
    ModelLookupsPackageUploadComponent,
    ModelPackageUploadComponent,
    ModelPlannedMaintenanceDetailsComponent,
    ModelStatusPipe,
    NameVersionPipe,
    NodeInputDataTypePipe,
    NodeOutputDataTypePipe,
    PackageUploadComponent,
    PerformanceStandardsDialogComponent,
    FailureEffectCommercialImpactDialogComponent,
    PlannedMaintenanceFailureModeSelectionDialogComponent,
    PlannedMaintenanceFrequencyCalendarValidator,
    PlannedMaintenanceFrequencyHoursRunValidator,
    PlannedMaintenanceFrequencyPipe,
    PlannedMaintenanceFrequencyUsageFactorValidator,
    PrecisionValidator,
    RangeValidator,
    RcmPackageUploadComponent,
    FailurePatternWeibullValidator,
    FailureEffectConsequenceValidator,
    FailureEffectCommentValidator,
    PrecisionValidator,
    RecordCountPipe,
    ReportDrawingsComponent,
    ReportTasksComponent,
    ReportWorkOrdersComponent,
    RiskMatrixGridComponent,
    SectionHeaderAddItemComponent,
    SectionHeaderComponent,
    SiteHierarchyItemSelectionComponent,
    PlannedMaintenanceIntervalPipe,
    PlannedMaintenanceActivityPipe,
    UsernameIsEmailValidator,
    ModelFunctionDetailsContainerComponent,
    PdfViewerComponent,
    ConsequenceRiskMatrixSelectionDialogComponent,
    ChildDataTableComponent,
    ChildDataTableMultiSelectionDialogComponent,
    DataUploadResultComponent,
    DataUploadWorksheetResultComponent,
    DataUploadMessageTableComponent,
    DataUploadRowMessageTableComponent,
    MaintenanceStrategyPlannedMaintenanceDialogComponent,
    MaintenanceStrategyPlannedMaintenanceTableComponent,
    MaintenanceStrategyAvailablePlannedMaintenanceTableComponent,
    MaintenanceAnalysisStatusPipe,
    BatchJobStatePipe,
    ErpSyncStatePipe,
    ErpSyncStateIconPipe,
    ErpSyncStateMessagePipe,
    RequirementTypePipe,
    XAxisLabelPipe,
    SiteDropdownListComponent,
    DynamicDetailsTreeComponent,
    FilterSearchBoxComponent,
    DisplayAppliedFilterComponent,
    SwitcherComponent,
    LoadingDropdownComponent,
    StatusLoaderComponent,
    YesNoPipe,
    TrueFalsePipe,
    InspectionHistoryIconComponent,
    ModalDialogComponent,
    InspectionHistoryButtonComponent,
    InspectionHistoryDialogComponent,
    DataUploadMessageSeveritySymbolComponent,
    MitigationPlanComponent,
    IconWithBadgeComponent,
    MitigationPlanTaskFrequencyPipe,
    IsoHierarchyQueryComponent,
    CommercialImpactItemComponent,
    CommercialImpactItemTypePipe,
    TimeConverterPipe,
    FailureRateUomPipe,
    FailureRateInputComponent,
    BatchJobTypePipe,
    ValidateOptimizationTypePipe,
    TranslateTerminologyPipe,
    ChartViewerComponent,
    IsIntValidator,
    ScenarioTypePipe,
    IsRestrictedRolePipe,
    InvalidNumberCellPipe,
    WidgetSettingsPipe,
    WidgetDateRangeValidatorPipe,
    NullUndefinedToStringPipe,
    CreateThicknessLabelPipe,
    IdmsBaseItemDetailsComponent,
    GetInspectionPlanTaskCellTextPipe,
    CreateNumericLabelPipe,
    GetInspectionPlanTaskCellTextPipe,
    ObjectDrawerFilterDialogComponent,
    MultiSelectComponent,
    SplitPipe,
    MapPipe,
    IdentifierColumnLabelPipe,
    IdentifierColumnDisabledPipe,
    ConfirmPasswordValidator,
    IsValidPasswordValidator,
    GenericDictionaryPipe,
    LanguageSelectorComponent,
    TMinLabelPipe,
    ThicknessReportTypePipe,
    ContentHeightDirective,
    AuditReportTableComponent,
    ReportHierarchyTableComponent,
    AuditEventApplicationDisplayTextPipe,
    HbTextboxInputComponent,
    HbNumberInputComponent,
    HbTextareaComponent,
    HbRadioButtonComponent,
    HbSingleSelectDropdownComponent,
    HbCheckboxComponent,
    CatalogSelectorComponent,
    EventsHistoryDialogComponent
  ],
  imports: [
    AccordionModule,
    ButtonModule,
    AutoCompleteModule,
    FeaturesModule,
    BootstrapImportsModule,
    CheckboxModule,
    CollapseModule.forRoot(),
    CommonModule,
    DropdownModule,
    NgxFileDropModule,
    FormsModule,
    InputTextModule,
    MessagesModule,
    HbSharedUiModule,
    ReactiveFormsModule,
    RouterModule,
    SharedModule,
    SpinnerModule,
    TabViewModule,
    ListboxModule,
    EditorModule,
    MultiSelectModule,
    PdfViewerModule,
    TableModule,
    TreeModule,
    NgSelectModule,
    OverlayPanelModule,
    Ng2GoogleChartsModule,
    RadioButtonModule,
    HbComponentLibraryModule,
    TooltipModule,
    InputTextareaModule,
    ...MaterialModules,
    I18NextModule.forRoot(),
  ],
  exports: [
    FailureRateUomPipe,
    TimeConverterPipe,
    TranslateTerminologyPipe,
    BootstrapImportsModule,
    ChartViewerComponent,
    ChildDataTableComponent,
    CommonModule,
    CommercialImpactsComponent,
    CommercialImpactInputsComponent,
    ConcatenateParentChildPipe,
    ConfirmationDialogComponent,
    ConditionForWorkPipe,
    DataUploadResultComponent,
    DataUploadWorksheetResultComponent,
    DateTimeFormatterPipe,
    DisplayModelDetailsComponent,
    DisplayModelsListComponent,
    DrawingPreviewComponent,
    DynamicAutoCompleteTextboxComponent,
    DynamicDetailsComponent,
    DynamicDetailsDialogComponent,
    DynamicDetailsListComponent,
    DynamicDetailsListItemComponent,
    DynamicDetailsSaveComponent,
    DynamicDetailsSectionContainerComponent,
    DynamicFormComponent,
    DynamicFormControlComponent,
    DynamicDateTimeComponent,
    DropdownComponent,
    DropdownDoubleComponent,
    DynamicRichTextComponent,
    TextareaComponent,
    TextboxComponent,
    DynamicSeparatorComponent,
    DynamicFormControlLabelComponent,
    ErpReferencePipe,
    EquipmentClassComponent,
    EquipmentFunctionModelSelectionCriteriaDialogComponent,
    FailureDefectsValidatorDirective,
    FailureModeParametersDialogComponent,
    FailurePatternOtherParamsValidator,
    FailurePatternWeibullValidator,
    FailureEffectConsequenceValidator,
    FailureEffectCommentValidator,
    FailureEffectCommercialImpactValidator,
    FailureRateUomValidatorDirective,
    FileSelectionComponent,
    FileUploadComponent,
    FilterTagComponent,
    FlattenedOrgTreeNodeListComponent,
    FormsModule,
    FunctionEditorComponent,
    HbMathJaxDirective,
    HierarchyTableComponent,
    IndustryHierarchyComponent,
    LicenceStatusPipe,
    LinkTemplatePipe,
    LookupCopyConfirmationDialogComponent,
    MaintenanceTypePipe,
    MediaActionsComponent,
    MediaButtonComponent,
    MediaComponent,
    MitigationsTableComponent,
    ModelLookupsPackageUploadComponent,
    ModelPackageUploadComponent,
    ModelStatusPipe,
    NameVersionPipe,
    NodeInputDataTypePipe,
    NodeOutputDataTypePipe,
    RangeValidator,
    PrecisionValidator,
    PackageUploadComponent,
    PerformanceStandardsDialogComponent,
    FailureEffectCommercialImpactDialogComponent,
    PlannedMaintenanceFrequencyCalendarValidator,
    PlannedMaintenanceFrequencyHoursRunValidator,
    PlannedMaintenanceFrequencyPipe,
    PlannedMaintenanceFrequencyUsageFactorValidator,
    PrecisionValidator,
    ReactiveFormsModule,
    RecordCountPipe,
    ReportDrawingsComponent,
    ReportTasksComponent,
    ReportWorkOrdersComponent,
    RiskMatrixGridComponent,
    SectionHeaderAddItemComponent,
    SectionHeaderComponent,
    SiteHierarchyItemSelectionComponent,
    SiteDropdownListComponent,
    UsernameIsEmailValidator,
    ModelFunctionDetailsComponent,
    ModelFunctionDetailsContainerComponent,
    ModelFunctionalFailureDetailsComponent,
    ModelFailureModeDetailsComponent,
    ModelFailureModeFailureMechanismDetailsComponent,
    ModelFailureMechanismReasonDetailsComponent,
    ModelFailureMechanismReasonFailureCauseDetailsComponent,
    ModelFailureEffectDetailsComponent,
    ModelFailureModeConsequenceClassificationComponent,
    ModelPlannedMaintenanceDetailsComponent,
    ConsequenceRiskMatrixSelectionDialogComponent,
    MaintenanceStrategyPlannedMaintenanceDialogComponent,
    MaintenanceStrategyPlannedMaintenanceTableComponent,
    MaintenanceStrategyAvailablePlannedMaintenanceTableComponent,
    PlannedMaintenanceIntervalPipe,
    MaintenanceAnalysisStatusPipe,
    BatchJobStatePipe,
    BatchJobTypePipe,
    ErpSyncStatePipe,
    ErpSyncStateIconPipe,
    ErpSyncStateMessagePipe,
    RequirementTypePipe,
    XAxisLabelPipe,
    DynamicDetailsTreeComponent,
    FilterSearchBoxComponent,
    DisplayAppliedFilterComponent,
    StatusLoaderComponent,
    InspectionHistoryIconComponent,
    ModalDialogComponent,
    InspectionHistoryButtonComponent,
    IsoHierarchyQueryComponent,
    InspectionHistoryDialogComponent,
    IconWithBadgeComponent,
    TabOrderDirective,
    MitigationPlanComponent,
    ValidateOptimizationTypePipe,
    IsIntValidator,
    ScenarioTypePipe,
    IsRestrictedRolePipe,
    InvalidNumberCellPipe,
    WidgetSettingsPipe,
    WidgetDateRangeValidatorPipe,
    IdmsBaseItemDetailsComponent,
    SplitPipe,
    ConfirmPasswordValidator,
    IsValidPasswordValidator,
    GenericDictionaryPipe,
    LanguageSelectorComponent,
    CatalogSelectorComponent,
    ContentHeightDirective,
    AuditReportTableComponent,
    ReportHierarchyTableComponent,
    HbTextboxInputComponent,
    HbNumberInputComponent,
    HbTextareaComponent,
    HbRadioButtonComponent,
    HbSingleSelectDropdownComponent,
    HbCheckboxComponent,
    EventsHistoryDialogComponent,
  ],
  providers: [
    HierarchyTemplateDetailsService,
    HierarchyTemplateAttributesTableService,
    HierarchyTemplateAttributesTableConfigService,
    HierarchyTemplatesService,
    HierarchyTemplatesNotificationService,
    AdminLookupService,
    AdminLookupTableService,
    AdminLookupTableConfigService,
    MitigationPlanService,
    ConditionForWorkPipe,
    ConsequenceAxisLabelService,
    ConfirmationDialogService,
    DateTimeFormatterPipe,
    DrawingPreviewGalleryService,
    DynamicDetailsListFilterService,
    DynamicDetailsListService,
    DynamicFormControlService,
    ExpressionValidatorService,
    FilterAttributeFactoryService,
    FunctionEditorService,
    HbmathjaxService,
    HierarchyDashboardDrilldownService,
    InspectionReportListService,
    MaintenanceTypePipe,
    MediaItemService,
    MessageService,
    ModelPackageService,
    PlannedMaintenanceFrequencyPipe,
    SectionHeaderService,
    ThicknessDisplayService,
    UomService,
    AdminModelConfigService,
    ModelFunctionDeletionService,
    DynamicFormControlViewsService,
    PlannedMaintenanceLibraryService,
    DrawingPreviewService,
    DrawingPreviewInspectionPointService,
    TableAttributeTemplateTableConfigService,
    TableAttributeTemplateTableService,
    TableAttributeTemplatesProviderService,
    TableAttributeTemplateService,
    DrawingPreviewNavigationService,
    WidgetDateRangeValidatorPipe,
    CreateThicknessLabelPipe,
    CreateNumericLabelPipe,
    IdentifierColumnLabelPipe,
    IdentifierColumnDisabledPipe,
    ThicknessReportTypePipe,
    {
      provide: I18NEXT_NAMESPACE,
      useValue: ['module.hb-shared', 'module.idms', 'module.component-library', 'module.mitigation', 'module-lookups', 'module-rbi'],
    },
    NodeInputDataTypePipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [
    ConfirmationDialogComponent,
    ChildDataTableMultiSelectionDialogComponent,
    DrawingPreviewGalleryComponent,
    EquipmentFunctionModelSelectionCriteriaDialogComponent,
    FailureEffectCommercialImpactDialogComponent,
    FailureModeParametersDialogComponent,
    LookupCopyConfirmationDialogComponent,
    PerformanceStandardsDialogComponent,
    PlannedMaintenanceFailureModeSelectionDialogComponent,
    ConsequenceRiskMatrixSelectionDialogComponent,
    MaintenanceStrategyPlannedMaintenanceDialogComponent,
    InspectionHistoryDialogComponent,
    ObjectDrawerFilterDialogComponent,
  ],
})
export class HbSharedModule { }
